import { Suspense, useRef } from "react";
import { useTranslation } from "react-i18next";

import type { FriendGroup } from "@/api/bale.zod";
import type { DragMoveEvent } from "@/components/drag-columns/context/DragProvider";

import { useGetFriendGroup, useListFriendGroupsSuspense, useUpdateFriendGroup } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Card, CardActionsHeader } from "@/components/Card";
import { DragColumn } from "@/components/drag-columns/components/DragColumn";
import { DragContainer } from "@/components/drag-columns/components/DragContainer";
import { DragItem } from "@/components/drag-columns/components/DragItem";
import { IconButton } from "@/components/IconButton";
import { Missing } from "@/components/Missing";
import { TextLoading } from "@/components/TextLoading";

import { CreateFriendGroupModal } from "./CreateFriendGroupModal";

type Props = {
  onSelectFriendGroup: (friendGroup: FriendGroup) => void;
  className?: string;
};

export function FriendGroups({ onSelectFriendGroup }: Props) {
  const { t } = useTranslation();
  const createFriendGroupModal = useRef<HTMLDialogElement>(null);

  function createdFriendGroup(friendGroup?: FriendGroup) {
    createFriendGroupModal.current?.close();
    if (friendGroup) {
      onSelectFriendGroup?.(friendGroup);
    }
  }

  return (
    <Card>
      <CardActionsHeader title={t("friend-groups")}>
        <IconButton type="button" name="plus-square" onClick={() => createFriendGroupModal.current?.showModal()} />
      </CardActionsHeader>
      <Suspense
        fallback={
          <div className="flex flex-col">
            <FriendGroupLoading />
            <FriendGroupLoading />
            <FriendGroupLoading />
          </div>
        }
      >
        <FriendGroupsContents onSelectFriendGroup={onSelectFriendGroup} />
      </Suspense>
      <CreateFriendGroupModal ref={createFriendGroupModal} onClose={createdFriendGroup} />
    </Card>
  );
}

function FriendGroupsContents({ onSelectFriendGroup }: { onSelectFriendGroup: (friendGroup: FriendGroup) => void }) {
  const { t } = useTranslation();
  const friendGroups = useListFriendGroupsSuspense({
    query: { select: (data) => data.data.friend_groups },
  });
  const updateFriendGroup = useUpdateFriendGroup();

  function handleReorder(move: DragMoveEvent) {
    const friendGroup = friendGroups.data[move.from.row];
    if (!friendGroup) return;
    updateFriendGroup.mutate({
      friendGroupId: friendGroup.id,
      data: { ...friendGroup, sort: move.to.row },
    });
  }

  return (
    <DragContainer onMove={handleReorder}>
      <DragColumn column={0}>
        {friendGroups.data.map((friendGroup, i) => (
          <DragItem drag="y" auto key={friendGroup.id} row={i} className="flex flex-col">
            <FriendGroupItem friendGroup={friendGroup} onClick={() => onSelectFriendGroup(friendGroup)} />
          </DragItem>
        ))}
        {friendGroups.data.length === 0 && <Missing className="mx-4 mb-4">{t("no-friend-groups")}</Missing>}
      </DragColumn>
    </DragContainer>
  );
}

function FriendGroupItem({ friendGroup, onClick }: { friendGroup: FriendGroup; onClick: () => void }) {
  const friendGroupMembers = useGetFriendGroup(friendGroup.id, { query: { select: (data) => data.data.members } });

  return (
    <div className="flex flex-row items-center bg-white p-2 pl-4">
      <div className="flex flex-col">
        <div className="flex flex-row items-baseline gap-4">
          <div className="font-interest">{friendGroup.name}</div>
          {friendGroupMembers.isSuccess && (
            <div className="font-hint text-placeholder">({friendGroupMembers.data?.length})</div>
          )}
        </div>
        <div className="font-hint text-placeholder">{friendGroup.description}</div>
      </div>
      <ActionBox className="ml-auto">
        <IconButton type="button" name="edit" onClick={onClick} />
      </ActionBox>
    </div>
  );
}

function FriendGroupLoading() {
  return (
    <div className="flex flex-col p-2 pl-4">
      <div className="font-interest">
        <TextLoading />
      </div>
      <div className="font-hint">
        <TextLoading className="w-[60ch]" />
      </div>
    </div>
  );
}
